/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiProvidersActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for AI providers list.
────────────────────────────────────────────────────────────────*/

import { Edit, Trash2, Copy } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { AiProvider } from '../api/aiProvidersApi';

interface AiProvidersActionsProps {
  provider: AiProvider;
  onEdit: (provider: AiProvider) => void;
  onDuplicate: (provider: AiProvider) => void;
  onDelete: (provider: AiProvider) => void;
}

export function AiProvidersActions({
  provider,
  onEdit,
  onDuplicate,
  onDelete
}: AiProvidersActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          type="button"
          onClick={() => onEdit(provider)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          aria-label={t('common.edit')}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          type="button"
          onClick={() => onDuplicate(provider)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          aria-label={t('common.duplicate')}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          type="button"
          onClick={() => onDelete(provider)}
          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

